import * as React from 'react';
import { cn } from '@/lib/utils';

const QuestionCard = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-question-card',
      // 1. LAYOUT & POSICIONAMENTO
      'mb-8 flex w-full flex-wrap items-start justify-between',
      // 2. ESTILIZAÇÃO VISUAL
      'rounded-xl border border-solid border-bright bg-white shadow-dropshadow',
      // 3. PADDING & RESPONSIVIDADE
      'p-4 md:p-8',
      className
    )}
    {...props}
  />
));
QuestionCard.displayName = 'QuestionCard';

const QuestionCardIntroduction = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-card-introduction', 'font-extrabold text-primary-dark', className)}
    {...props}
  />
));
QuestionCardIntroduction.displayName = 'QuestionCardIntroduction';

const QuestionCardPrint = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-card-print', 'text-sm italic text-neutral', className)}
    {...props}
  />
));
QuestionCardPrint.displayName = 'QuestionCardPrint';

const QuestionCardStatement = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-card-statement', 'w-full', 'text-primary-dark', 'font-bold', 'border-t border-solid border-primary-lightest', 'mt-4', 'pt-4', className)}
    {...props}
  />
));
QuestionCardStatement.displayName = 'QuestionCardStatement';

const QuestionCardAnswerWrapper = React.forwardRef(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-question-card-answers',
      'mt-4 w-full p-4 pt-0 text-[1.05em]',
      // Estes estilos afetam elementos filhos diretos ou aninhados dentro do div das respostas.
      '[&_i]:italic',
      // Ajuste de listas
      '[&_ul]:pl-7',
      '[&_ol]:pl-7',
      // Ajuste de cabeçalhos internos
      '[&_h2]:text-2xl',
      '[&_h3]:text-xl',
      '[&_h4]:text-lg',
      className
    )}
    {...props}
  >
    {children}
  </div>
));
QuestionCardAnswerWrapper.displayName = 'QuestionCardAnswerWrapper';

const QuestionCardAnswer = React.forwardRef(({ className, isCorrect = false, ...props }, ref) => {
  const containerLayoutClasses = cn(
    'c-question-card-answer',
    'flex items-center flex-nowrap gap-2',
    'min-w-0',
    'my-4',
    'w-full',
    // Estilo condicional para resposta correta
    isCorrect && 'text-secondary',
    className
  );

  // --- Estilos Aninhados para Elementos Filhos ---

  // 1. Estilos do <label> aninhado:
  const labelNestedClasses = cn(
    // Estilos diretos do label
    '[&_label]:leading-normal',
    '[&_label]:text-[.88em]',
    '[&_label]:leading-[1.2em]',
    '[&_label]:text-neutral-700',
    '[&_label]:cursor-pointer',
    // Estilos de pseudo-elementos e filhos
    '[&_label]:first-letter:capitalize',
    '[&_label>*:last-child]:mb-0',
    // Estilo condicional
    isCorrect && '[&_label]:text-secondary'
  );

  // 2. Estilos do <input> aninhado:
  const inputNestedClasses = cn(
    // Estilos diretos do input
    '[&_input]:m-0',
    '[&_input]:h-4',
    '[&_input]:shrink-0'
  );

  return (
    <div
      ref={ref}
      className={cn(containerLayoutClasses, labelNestedClasses, inputNestedClasses)}
      {...props}
    />
  );
});
QuestionCardAnswer.displayName = 'QuestionCardAnswer';

const QuestionCardAnswerLetter = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-card-answer-letter', 'shrink-0', 'min-w-4', 'text-[.7em]', 'font-extrabold', '-translate-x-1', 'select-none', 'uppercase', className)}
    {...props}
  />
));
QuestionCardAnswerLetter.displayName = 'QuestionCardAnswerLetter';

const QuestionCardCta = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-card-cta', 'mb-4', className)}
    {...props}
  />
));
QuestionCardCta.displayName = 'QuestionCardCta';

const QuestionCardComment = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-card-comment', 'w-full', 'bg-white', 'rounded-xl', 'mt-8', 'mb-8', className)}
    {...props}
  />
));
QuestionCardComment.displayName = 'QuestionCardComment';

const QuestionCardTextMotivator = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'c-question-card-text-motivator',
      'bg-bright/50 my-4 w-full rounded-[5px] border border-solid border-bright p-4',
      'break-words text-[0.9em] leading-normal',
      '[&_p]:m-0',
      '[&_img]:max-h-[550px] [&_img]:w-auto [&_img]:max-w-full',
      '[&_.ck-editor_i]:italic [&_i]:italic',
      '[&_ul]:list-disc [&_ul]:pl-[30px]',
      '[&_ol]:list-decimal [&_ol]:pl-[30px]',
      '[&_h2]:text-[2em] [&_h3]:text-[1.6em] [&_h4]:text-[1.2em]',
      className
    )}
    {...props}
  />
));
QuestionCardTextMotivator.displayName = 'QuestionCardTextMotivator';

const QuestionCardTitle = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-card-title', 'my-4', className)}
    {...props}
  />
));
QuestionCardTitle.displayName = 'QuestionCardTitle';

const QuestionCardNav = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('c-question-card-nav', 'mt-10 flex flex-wrap items-center justify-between gap-4', className)}
    {...props}
  />
));
QuestionCardNav.displayName = 'QuestionCardNav';

export {
  QuestionCard,
  QuestionCardIntroduction,
  QuestionCardPrint,
  QuestionCardStatement,
  QuestionCardAnswerWrapper,
  QuestionCardAnswer,
  QuestionCardAnswerLetter,
  QuestionCardCta,
  QuestionCardComment,
  QuestionCardTextMotivator,
  QuestionCardTitle,
  QuestionCardNav
};
